<template>
  <parent>
    <div class="healthchecks-form-wrapper h-100">
      <loading-page v-if="loading" />
      <form-wrapper v-else>
        <template v-slot:body>
          <validation-observer
            ref="formRules"
            tag="form"
          >
            <paper-simple>
              <validation-provider
                #default="{ errors }"
                name="Name"
                rules="required"
              >
                <input-field
                  label="Name*"
                  label-position="top"
                  class="mr-35"
                  :error="errors.length ? errors[0] : null"
                >
                  <b-form-input
                    v-model="healthcheck.name"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
                <custom-alert
                  class="mr-35"
                  items-center
                  icon-name="InfoAlt"
                >
                  A name to describe this healthcheck.
                </custom-alert>
              </validation-provider>
              <validation-provider
                #default="{ errors }"
                name="Request"
                rules="required"
              >
                <input-field
                  label="Request"
                  :info="messages.healthcheckRequest"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  @openInfo="
                    showInfo(
                      messages.healthcheckRequest.title,
                      messages.healthcheckRequest.message,
                    )
                  "
                >
                  <custom-select
                    v-model="healthcheck.requestMethod"
                    class="r-dropdown"
                    :state="errors.length > 0 ? false : null"
                    :options="getOptions(healthcheck.requestMethodOptions)"
                  />
                  <b-form-input
                    v-model="healthcheck.requestPath"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
              <validation-provider
                #default="{ errors }"
                name="Host"
                rules="required"
              >
                <input-field
                  label="Host *"
                  label-position="top"
                  class="mr-35"
                  :error="errors.length ? errors[0] : null"
                >
                  <b-form-input
                    v-model="healthcheck.host"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
                <custom-alert
                  class="mr-35"
                  items-center
                  icon-name="InfoAlt"
                >
                  Host header to set when making the request (e.g. example.com)
                </custom-alert>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                name="Expected Response"
                rules="required"
              >
                <input-field
                  label="Expected Response"
                  class="mr-35"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                >
                  <v-select
                    v-model="expectedResponseOption"
                    :state="errors.length > 0 ? false : null"
                    class="flex-fill distro-single-select"
                    :options="expectedResponseOptions"
                    placeholder="Select Request Method"
                  />
                </input-field>
              </validation-provider>
              <custom-alert
                class="mr-35"
                items-center
                icon-name="InfoAlt"
              >
                The HTTP status code to that the probe expects. The default
                value is 200.
              </custom-alert>
            </paper-simple>

            <paper-simple>
              <input-field
                v-if="!details"
                label="Check frequency"
                label-position="top"
              >
                <b-form-radio-group
                  v-model="frequency"
                  :options="frequencyOptions"
                  stacked
                  @change="changeFrequency"
                />
              </input-field>
              <validation-provider
                #default="{ errors }"
                name="Threshold & Window"
                rules="required"
              >
                <input-field
                  label="Threshold & Window *"
                  :info="messages.healthcheckThreshold"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  @openInfo="
                    showInfo(
                      messages.healthcheckThreshold.title,
                      messages.healthcheckThreshold.message,
                    )
                  "
                >
                  <b-form-input
                    v-model="healthcheck.threshold"
                    :disabled="!details && frequency !== 3"
                    class="mr-1"
                    :state="errors.length > 0 ? false : null"
                  />
                  /
                  <b-form-input
                    v-model="healthcheck.window"
                    :disabled="!details && frequency !== 3"
                    class="ml-1"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
              <validation-provider
                #default="{ errors }"
                name="Initial"
                rules="required"
              >
                <input-field
                  label="Initial *"
                  label-position="top"
                  :info="messages.healthcheckInitial"
                  :error="errors.length ? errors[0] : null"
                  @openInfo="
                    showInfo(
                      messages.healthcheckInitial.title,
                      messages.healthcheckInitial.message,
                    )
                  "
                >
                  <b-form-input
                    v-model="healthcheck.initial"
                    :disabled="!details && frequency !== 3"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
              <validation-provider
                #default="{ errors }"
                name="Interval & Timeout"
                rules="required"
              >
                <input-field
                  label="Interval & Timeout (ms) *"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  :info="messages.healthcheckInterval"
                  @openInfo="
                    showInfo(
                      messages.healthcheckInterval.title,
                      messages.healthcheckInterval.message,
                    )
                  "
                >
                  <b-form-input
                    v-model="healthcheck.interval"
                    :disabled="!details && frequency !== 3"
                    class="mr-1"
                    :state="errors.length > 0 ? false : null"
                  />
                  /
                  <b-form-input
                    v-model="healthcheck.timeout"
                    :disabled="!details && frequency !== 3"
                    class="ml-1"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
            </paper-simple>
          </validation-observer>
        </template>
        <template v-slot:footer>
          <b-button
            variant="bg-white"
            @click="$emit('close')"
          >
            Cancel
          </b-button>
          <button-loading
            variant="bg-primary"
            class="ml-16"
            :spinning="details ? updatingHealthcheck : addingHealthcheck"
            @click="healthcheckValidation"
          >
            {{ details ? 'Update' : 'Create' }}
          </button-loading>
        </template>
      </form-wrapper>
      <info
        v-if="openInfo"
        :title="infoTitle"
        :message="infoMessage"
        @close="openInfo = false"
      />
    </div>
  </parent>
</template>
<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import redirectError from '@/services/redirectError'
import messages from '../../../messages'
import vSelect from 'vue-select'

export default {
  name: 'DistributionHealthcheckForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    healthcheckId: {
      type: [String, Number],
      default: null,
    },
    distributionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      required,
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      frequency: 1,
      frequencyOptions: [
        {
          text: 'Low',
          value: 0,
        },
        {
          text: 'Medium',
          value: 1,
        },
        {
          text: 'High',
          value: 2,
        },
        {
          text: 'Custom',
          value: 3,
        },
      ],
      timeout: 5000,
      windows: [2, 5, 10],
      intervals: [60000, 15000, 2000],
      thresholds: [1, 3, 7],
      initials: [1, 4, 7],
      expectedResponseOption: null,
    }
  },
  computed: {
    healthcheck() {
      return this.$store.state.cdn.healthcheck
    },
    loading() {
      return this.$store.state.cdn.gettingHealthcheck
    },
    updatingHealthcheck() {
      return this.$store.state.cdn.updatingHealthcheck
    },
    addingHealthcheck() {
      return this.$store.state.cdn.addingHealthcheck
    },
    expectedResponseOptions() {
      return this.healthcheck
        ? this.healthcheck.expectedResponseOptions.map(el => ({
          value: el.id,
          label: `${el.id}: ${el.value}`,
        }))
        : []
    },
    expectedResponseLabel() {
      return this.expectedResponseOptions.find(
        el => el.value === this.healthcheck.expectedResponse,
      ).label
    },
  },
  created() {
    this.$store
      .dispatch('cdn/getHealthcheck', {
        distributionId: this.distributionId,
        id: this.details ? this.healthcheckId : 0,
      })
      .then(() => {
        this.expectedResponseOption = {
          value: this.healthcheck.expectedResponse,
          label: this.expectedResponseLabel,
        }
        if (!this.details) {
          this.healthcheck.distributionId = this.distributionId
        }
      })
      .catch(err => redirectError(err, this.$router, this.$route.path))
  },
  methods: {
    healthcheckValidation() {
      return new Promise((resolve, reject) => {
        this.$refs.formRules.validate().then(success => {
          if (success) {
            this.onSave()
            resolve()
          } else {
            reject()
          }
        })
      })
    },
    onSave() {
      this.healthcheck.expectedResponse = this.expectedResponseOption.value
      this.$emit('submit', this.healthcheck)
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
    changeFrequency(val) {
      if (val !== 3) {
        this.healthcheck.threshold = this.thresholds[val]
        this.healthcheck.window = this.windows[val]
        this.healthcheck.interval = this.intervals[val]
        this.healthcheck.initial = this.initials[val]
        this.healthcheck.timeout = this.timeout
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.healthchecks-form-wrapper {
  ::v-deep {
    .form-wrapper {
      &__body {
        > div {
          margin: auto;
        }
      }
    }
  }
  .mr-35 {
    margin-right: 35px;
  }
  .r-dropdown {
    width: 200px !important;
  }
}
</style>
